import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PageSeo from "components/Seo/PageSeo";
import { faqPageSchema, sportsEventSchema } from "helpers/seo/schema";
import { buildMeetSeoMeta, buildMeetSportsEventSchema } from "helpers/seo/buildMeetSeo";
import meetLogo from "assets/meetLogo.png";
import meetHero from "assets/meetHero.png";
import mobileMeetHero from "assets/mobileMeetHero.png";
import { buildMeetPageData, resolveMeetRegistrationUrl } from "helpers/contentful/normalizeGymnasticMeet";
import { buildVenueAddressQuery } from "helpers/geocode/meetPlaceDistance";
import { contentfulFieldToHtml, resolveVisitBardstownHtml } from "helpers/contentful/contentfulFieldToHtml";
import { AnalyticsEvents, trackEvent } from "helpers/analytics/analytics";
import MeetFaq from "./MeetFaq";
import MeetSectionView from "./MeetSectionView";
import {
  MeetAdmissionTable,
  MeetContactCards,
  MeetFeeCards,
  MeetRegistrationSteps,
  MeetSponsorCard,
  MeetVenueBlock,
  MeetVisitSection,
} from "./MeetStructuredContent";
import { MEET_VENUE } from "./meetPageContent";
import useVisitPlaceDistances from "./useVisitPlaceDistances";
import "./meetExperience.css";

const DEFAULT_SLUG = "bourbon-city-spectacular-2025";

const CONTENT_SECTIONS = [
  { id: "location", label: "Location", field: "location" },
  { id: "entry-fees", label: "Entry & Admission" },
  { id: "registration", label: "Registration" },
  { id: "contacts", label: "Contacts" },
];

const JUMP_LINKS = [
  { href: "#highlights", label: "Highlights" },
  { href: "#visit-bardstown", label: "Bardstown" },
  { href: "#gallery", label: "Photos" },
  { href: "#meet-details", label: "Details" },
  { href: "#faq", label: "FAQ" },
];

const MeetRichBlock = ({ html, className }) => {
  if (!html) return null;
  return (
    <div
      className={`meet-x__richtext ${className || ""}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

/** Hides raw JSON accidentally pasted into a rich text field. */
const meetProseHtml = (html) => {
  if (!html) return "";
  const text = html.replace(/<[^>]*>/g, "").trim();
  if (!text.startsWith("[") && !text.startsWith("{")) return html;
  try {
    JSON.parse(text);
    return "";
  } catch {
    return html;
  }
};

const sectionHasContent = (section, fields, pageData) => {
  switch (section.id) {
    case "location": {
      const html = meetProseHtml(contentfulFieldToHtml(fields?.[section.field]));
      return !!html || Boolean(pageData.venue?.name);
    }
    case "entry-fees":
      return (
        pageData.meetFees?.length > 0 || pageData.admissionPrices?.length > 0
      );
    case "registration":
      return pageData.registrationSteps?.length > 0;
    case "contacts":
      return pageData.contactsList?.length > 0;
    default:
      return false;
  }
};

const MeetPanelBody = ({ section, fields, pageData, venue, venueMapEmbedUrl }) => {
  const html =
    section.field != null
      ? meetProseHtml(contentfulFieldToHtml(fields[section.field]))
      : "";

  return (
    <div className="meet-x__panel-body">
      {section.id === "location" && (
        <>
          <MeetRichBlock html={html} />
          <MeetVenueBlock venue={venue} mapEmbedUrl={venueMapEmbedUrl} />
        </>
      )}

      {section.id === "entry-fees" && (
        <>
          <MeetFeeCards items={pageData.meetFees} />
          <MeetAdmissionTable items={pageData.admissionPrices} />
        </>
      )}

      {section.id === "registration" && (
        <MeetRegistrationSteps steps={pageData.registrationSteps} />
      )}

      {section.id === "contacts" && (
        <MeetContactCards items={pageData.contactsList} />
      )}
    </div>
  );
};

const MeetExperience = ({ preferredSlug = DEFAULT_SLUG }) => {
  const gymnasticMeets = useSelector(
    (state) => state.contentfulReducer.gymnasticMeets
  );
  const [heroSrc, setHeroSrc] = useState(meetHero);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const heroRef = useRef(null);
  const finaleRef = useRef(null);
  const stickyViewTracked = useRef(false);

  const meet = useMemo(() => {
    if (!gymnasticMeets?.length) return null;
    return (
      gymnasticMeets.find((m) => m.fields.slug === preferredSlug) ||
      gymnasticMeets.find((m) =>
        m.fields.slug?.includes(preferredSlug.replace(/-20\d{2}$/, ""))
      ) ||
      gymnasticMeets[0]
    );
  }, [gymnasticMeets, preferredSlug]);

  const fields = meet?.fields;

  const meetId = meet?.sys?.id;

  const pageData = useMemo(() => {
    if (!fields) return null;
    const built = buildMeetPageData(fields, {});
    const data = fields.pageData || built;
    return {
      ...built,
      ...data,
      registrationUrl:
        data.registrationUrl || resolveMeetRegistrationUrl(fields.registrationLink),
      venue: data.venue || built.venue,
      meetFees: data.meetFees?.length ? data.meetFees : built.meetFees,
      admissionPrices: data.admissionPrices?.length
        ? data.admissionPrices
        : built.admissionPrices,
      registrationSteps: data.registrationSteps?.length
        ? data.registrationSteps
        : built.registrationSteps,
      contactsList: data.contactsList?.length
        ? data.contactsList
        : built.contactsList,
      visit: built.visit,
    };
  }, [fields]);

  const { visit: visitForSection, distancesLoading } = useVisitPlaceDistances(
    pageData?.visit,
    pageData?.venue || MEET_VENUE
  );

  useEffect(() => {
    const desktop = pageData?.heroImageUrl || meetHero;
    const mobile = pageData?.heroImageUrl || mobileMeetHero;
    const updateHero = () => {
      setHeroSrc(window.innerWidth < 450 ? mobile : desktop);
    };
    updateHero();
    window.addEventListener("resize", updateHero);
    return () => window.removeEventListener("resize", updateHero);
  }, [pageData?.heroImageUrl]);

  useEffect(() => {
    const hero = heroRef.current;
    const finale = finaleRef.current;
    if (!hero || !meetId) return undefined;

    let heroInView = true;
    let finaleInView = false;

    const updateSticky = () => {
      setShowStickyCta(!heroInView && !finaleInView);
    };

    const observerOptions = { threshold: 0.12 };

    const heroObserver = new IntersectionObserver(([entry]) => {
      heroInView = entry.isIntersecting;
      updateSticky();
    }, observerOptions);

    heroObserver.observe(hero);

    let finaleObserver;
    if (finale) {
      finaleObserver = new IntersectionObserver(([entry]) => {
        finaleInView = entry.isIntersecting;
        updateSticky();
      }, observerOptions);
      finaleObserver.observe(finale);
    }

    return () => {
      heroObserver.disconnect();
      finaleObserver?.disconnect();
    };
  }, [meetId]);

  useEffect(() => {
    if (!showStickyCta || stickyViewTracked.current) return;
    stickyViewTracked.current = true;
    trackEvent(AnalyticsEvents.MEET_STICKY_VIEW, { location: "meet_page" });
  }, [showStickyCta]);

  const descriptionHtml = contentfulFieldToHtml(fields?.description);
  const visitIntroHtml = meetProseHtml(resolveVisitBardstownHtml(fields));
  const logoSrc = meetLogo;

  const hasDetailSections =
    pageData &&
    CONTENT_SECTIONS.some((s) => sectionHasContent(s, fields, pageData));

  if (!gymnasticMeets.length) {
    return (
      <div className="meet-x meet-x--loading">
        <p>Loading meet information…</p>
      </div>
    );
  }

  if (!meet || !pageData) {
    return (
      <div className="meet-x meet-x--loading">
        <p>Meet information is not available yet. Check back soon.</p>
      </div>
    );
  }

  const registrationUrl = pageData.registrationUrl;
  const venue = pageData.venue || MEET_VENUE;
  const venueQuery = buildVenueAddressQuery(venue);
  const venueMapEmbedUrl =
    venue.mapEmbedUrl ||
    `https://maps.google.com/maps?q=${venue.mapQuery}&output=embed`;

  const meetSeo = buildMeetSeoMeta({
    fields,
    pageData,
    venue,
    preferredSlug,
  });
  const meetOgImage = pageData.heroImageUrl?.startsWith("http")
    ? pageData.heroImageUrl
    : undefined;
  const meetSchema = [
    sportsEventSchema(
      buildMeetSportsEventSchema({
        fields,
        pageData,
        venue,
        preferredSlug,
        registrationUrl,
      })
    ),
    faqPageSchema(pageData.faqs),
  ];

  return (
    <div className="meet-x">
      <PageSeo
        title={meetSeo.title}
        description={meetSeo.description}
        path={meetSeo.path}
        ogImage={meetOgImage}
        ogImageAlt={`${fields.title} — gymnastics meet in Bardstown, Kentucky`}
        ogType={meetSeo.ogType}
        jsonLd={meetSchema}
      />

      <div
        className={`meet-x__sticky-cta${showStickyCta ? " is-visible" : ""}`}
        aria-hidden={!showStickyCta}
      >
        <a
          href={registrationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="meet-x__cta meet-x__cta--sticky"
          tabIndex={showStickyCta ? undefined : -1}
          data-track="meet_register_click"
          data-track-location="sticky"
        >
          Register now
        </a>
      </div>

      <header className="meet-x__hero" ref={heroRef}>
        <img
          className="meet-x__hero-bg"
          src={heroSrc}
          alt={`${fields.title} gymnastics meet at Ace Gymnastics in Bardstown, Kentucky`}
          width="1200"
          height="600"
        />
        <div className="meet-x__hero-overlay" />
        <div className="meet-x__hero-inner">
          <img
            className="meet-x__logo"
            src={logoSrc}
            alt={`${fields.title} logo`}
            width="400"
            height="200"
          />
          <p className="meet-x__eyebrow">{pageData.eyebrow}</p>
          <h1 className="meet-x__title">{fields.title}</h1>
          {meetSeo.dates.dateLabel ? (
            <div className="meet-x__hero-actions">
              <div className="meet-x__dates-pill">
                <p>{meetSeo.dates.dateLabel}</p>
              </div>
              <a
                href={registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="meet-x__cta meet-x__cta--hero"
                data-track="meet_register_click"
                data-track-location="hero"
              >
                Register for the meet
              </a>
            </div>
          ) : (
            <a
              href={registrationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="meet-x__cta meet-x__cta--hero"
              data-track="meet_register_click"
              data-track-location="hero"
            >
              Register for the meet
            </a>
          )}
        </div>
        <div className="meet-x__hero-accent" aria-hidden="true" />
      </header>

      <section className="meet-x__stats" aria-label="Meet at a glance">
        <div className="meet-x__stats-inner">
          {pageData.quickStats.map((stat) => (
            <div key={stat.label} className="meet-x__stat">
              <span className="meet-x__stat-value">{stat.value}</span>
              <span className="meet-x__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <nav className="meet-x__jump" aria-label="Page sections">
        <div className="meet-x__jump-inner">
          {JUMP_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="meet-x__jump-link"
              data-track="meet_section_nav_click"
              data-track-location="jump_nav"
              data-track-label={link.label}
            >
              {link.label}
            </a>
          ))}
          <a
            href={registrationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="meet-x__jump-link meet-x__jump-link--register"
            data-track="meet_register_click"
            data-track-location="jump_nav"
          >
            Register
          </a>
        </div>
      </nav>

      {descriptionHtml && (
        <MeetSectionView
          as="section"
          sectionId="intro"
          className="meet-x__intro meet-x__band--white"
          aria-labelledby="meet-intro"
        >
          <div className="meet-x__container meet-x__container--wide">
            <h2 id="meet-intro" className="meet-x__section-label meet-x__section-label--dark">
              {pageData.introHeading}
            </h2>
            <MeetRichBlock html={descriptionHtml} className="meet-x__intro-copy meet-x__intro-copy--dark" />
          </div>
        </MeetSectionView>
      )}

      <MeetSectionView
        as="section"
        sectionId="highlights"
        id="highlights"
        className="meet-x__highlights meet-x__band--black"
        aria-labelledby="highlights-heading"
      >
        <div className="meet-x__container meet-x__container--wide">
          <h2 id="highlights-heading" className="meet-x__section-label">
            {pageData.highlightsHeading}
          </h2>
          <div className="meet-x__highlight-grid">
            {pageData.highlights.map((item) => (
              <article key={item.title} className="meet-x__highlight-card">
                <span className="meet-x__highlight-mark" aria-hidden="true" />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </MeetSectionView>

      <MeetSectionView
        as="section"
        sectionId="visit-bardstown"
        id="visit-bardstown"
        className="meet-x__visit-section meet-x__band--light"
        aria-labelledby="visit-heading"
      >
        <MeetVisitSection
          visit={visitForSection}
          introHtml={visitIntroHtml}
          distancesLoading={distancesLoading}
          venueQuery={venueQuery}
        />
      </MeetSectionView>

      {pageData.gallery.length > 0 && (
        <MeetSectionView
          as="section"
          sectionId="gallery"
          id="gallery"
          className="meet-x__gallery meet-x__band--white"
          aria-labelledby="gallery-heading"
        >
          <div className="meet-x__container meet-x__container--wide">
            <h2 id="gallery-heading" className="meet-x__section-label meet-x__section-label--dark">
              {pageData.galleryHeading}
            </h2>
            <div className="meet-x__gallery-grid">
              {pageData.gallery.map((photo) => (
                <figure key={photo.src} className="meet-x__gallery-item">
                  <img src={photo.src} alt={photo.alt} loading="lazy" width="600" height="400" />
                  {photo.caption && <figcaption>{photo.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </div>
        </MeetSectionView>
      )}

      {hasDetailSections && (
        <MeetSectionView as="div" sectionId="meet-details" id="meet-details" className="meet-x__sections">
          {CONTENT_SECTIONS.filter((s) => sectionHasContent(s, fields, pageData)).map(
            (section, index) => (
              <MeetSectionView
                as="section"
                sectionId={section.id}
                key={section.id}
                id={section.id}
                className={`meet-x__panel ${
                  index % 2 === 0 ? "meet-x__panel--black" : "meet-x__panel--red"
                }`}
                aria-labelledby={`heading-${section.id}`}
              >
                <div className="meet-x__container meet-x__container--wide">
                  <h2 id={`heading-${section.id}`} className="meet-x__section-label">
                    {section.label}
                  </h2>
                  <MeetPanelBody
                    section={section}
                    fields={fields}
                    pageData={pageData}
                    venue={venue}
                    venueMapEmbedUrl={venueMapEmbedUrl}
                  />
                </div>
              </MeetSectionView>
            )
          )}
        </MeetSectionView>
      )}

      {pageData.faqs.length > 0 && (
        <MeetSectionView
          as="section"
          sectionId="faq"
          id="faq"
          className="meet-x__faq meet-x__band--black"
          aria-labelledby="faq-heading"
        >
          <div className="meet-x__container meet-x__container--wide">
            <h2 id="faq-heading" className="meet-x__section-label">
              {pageData.faqHeading}
            </h2>
            {pageData.faqIntro && (
              <p className="meet-x__faq-intro">{pageData.faqIntro}</p>
            )}
            <MeetFaq items={pageData.faqs} />
          </div>
        </MeetSectionView>
      )}

      {pageData.sponsors.length > 0 && (
        <MeetSectionView
          as="section"
          sectionId="sponsors"
          id="sponsors"
          className="meet-x__sponsors meet-x__band--light"
          aria-labelledby="sponsors-heading"
        >
          <div className="meet-x__container meet-x__container--wide meet-x__sponsors-inner">
            <h2
              id="sponsors-heading"
              className="meet-x__section-label meet-x__section-label--dark"
            >
              {pageData.sponsorsHeading}
            </h2>
            {pageData.sponsorsNote && (
              <p className="meet-x__sponsors-note">{pageData.sponsorsNote}</p>
            )}
            <div
              className={`meet-x__sponsor-grid${
                pageData.sponsors.length > 5 ? " meet-x__sponsor-grid--compact" : ""
              }`}
            >
              {pageData.sponsors.map((sponsor) => (
                <MeetSponsorCard key={sponsor.name} sponsor={sponsor} />
              ))}
            </div>
          </div>
        </MeetSectionView>
      )}

      <MeetSectionView
        as="section"
        sectionId="social"
        className="meet-x__social meet-x__band--red"
        aria-label="Follow the meet"
      >
        <div className="meet-x__container meet-x__social-inner">
          <h2 className="meet-x__social-title">{pageData.socialHeading}</h2>
          <p>{pageData.socialText}</p>
          <div className="meet-x__social-links">
            <a
              href={pageData.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-track="meet_social_click"
              data-track-location="meet_page"
              data-track-label="instagram"
            >
              Instagram
            </a>
            <a
              href={pageData.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-track="meet_social_click"
              data-track-location="meet_page"
              data-track-label="facebook"
            >
              Facebook
            </a>
          </div>
        </div>
      </MeetSectionView>

      <MeetSectionView
        as="section"
        sectionId="register"
        sectionRef={finaleRef}
        id="register"
        className="meet-x__finale meet-x__band--black"
        aria-label="Register"
      >
        <div className="meet-x__container meet-x__finale-inner">
          <h2 className="meet-x__finale-title">{pageData.closingTitle}</h2>
          <p className="meet-x__finale-text">
            {pageData.closingText.replace("{title}", fields.title)}
          </p>
          <a
            href={registrationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="meet-x__cta meet-x__cta--finale"
            data-track="meet_register_click"
            data-track-location="finale"
          >
            Register now
          </a>
        </div>
      </MeetSectionView>
    </div>
  );
};

export default MeetExperience;
